import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  DollarSign,
  Clock,
  CheckCircle2,
  Wallet,
  Inbox,
  History,
  FileText,
} from 'lucide-react'
import toast from 'react-hot-toast'
import commissionService from '@/services/commissionService'
import api from '@/services/api'
import { useAuthStore } from '@/store/authStore'
import { convencion } from '@/lib/pais'
import partnerService from '@/services/partnerService'
import PageContainer from '@/components/common/PageContainer'
import PageHeader from '@/components/common/PageHeader'
import KpiCard from '@/components/KpiCard'
import Pagination from '@/components/common/Pagination'
import DataTable from '@/components/common/DataTable'
import { EmptyState } from '@/components/common/EmptyState'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Textarea } from '@/components/ui/textarea'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'

// Tipo de comisión en palabras del asesor (docs/179: el gerente también ve su override).
const TIPO = {
  initial: 'Inicial',
  recurring: 'Recurrente',
  override_gerente: 'Override de equipo',
  country_override: 'Override de país',
  adjustment: 'Ajuste',
}

// Cómo se paga en cada país (política comercial): sin modalidad, el retiro no está habilitado.
const MODALIDAD = {
  matriz_sv: 'Se te paga desde la matriz de El Salvador, con FSE o con tu CCF.',
  sucursal_local: 'Se te paga por la sucursal de tu país.',
  transferencia_internacional: 'Se te paga por transferencia.',
}

const statusVariants = {
  pending: 'warning',
  pending_approval: 'warning',
  approved: 'default',
  paid: 'success',
  cancelled: 'danger',
  rejected: 'danger',
}

export default function Commissions() {
  const navigate = useNavigate()
  const { partner } = useAuthStore()
  const esCd2b = partner?.organizationType === 'cd2b_consultant'
  const esGerente = partner?.rolCd2b === 'gerente'
  const [params, setParams] = useSearchParams()
  // Gerente: «Mis ventas» (como asesor) u «Override de equipo». Se guarda en la URL.
  const ver = esGerente && params.get('ver') === 'override' ? 'override' : 'ventas'
  const [politica, setPolitica] = useState(null)
  const [commissions, setCommissions] = useState([])
  const [summary, setSummary] = useState(null)
  const [dashboard, setDashboard] = useState(null)
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [total, setTotal] = useState(0)
  const [payoutOpen, setPayoutOpen] = useState(false)
  const [payoutNotes, setPayoutNotes] = useState('')
  const [requesting, setRequesting] = useState(false)

  useEffect(() => {
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, ver])

  useEffect(() => {
    if (!esCd2b) return
    api.get('/partners/cd2b/mi-politica').then((r) => setPolitica(r.data?.data || null)).catch(() => setPolitica(null))
  }, [esCd2b])

  const fetchData = async () => {
    setLoading(true)
    try {
      const [commissionsData, summaryData, dashboardData] = await Promise.all([
        commissionService.getCommissions(esGerente ? { tipo: ver } : {}, { page: page + 1, limit: rowsPerPage }),
        commissionService.getSummary(),
        partnerService.getDashboard().catch(() => null),
      ])
      setCommissions(commissionsData.commissions || [])
      setTotal(commissionsData.pagination?.total || 0)
      setSummary(summaryData.data)
      setDashboard(dashboardData?.data || null)
    } catch {
      toast.error('Error al cargar comisiones')
    } finally {
      setLoading(false)
    }
  }

  const handleRequestPayout = async () => {
    setRequesting(true)
    try {
      await commissionService.requestPayout(payoutNotes)
      toast.success('Solicitud de pago enviada')
      setPayoutOpen(false)
      setPayoutNotes('')
      fetchData()
    } catch (err) {
      setPayoutOpen(false)
      toast.error(err.response?.data?.message || 'Error al solicitar pago', { duration: 8000 })
    } finally {
      setRequesting(false)
    }
  }

  const centsToDollars = (cents) => (cents || 0) / 100

  const kpis = [
    {
      title: 'Total generado',
      value: commissionService.formatCurrency(centsToDollars(summary?.totalGenerated)),
      icon: <DollarSign size={20} />,
      tone: 'primary',
    },
    {
      title: 'Pendiente aprobación',
      value: commissionService.formatCurrency(centsToDollars(summary?.pendingApproval)),
      icon: <Clock size={20} />,
      tone: 'warning',
    },
    {
      title: 'Aprobado',
      value: commissionService.formatCurrency(centsToDollars(summary?.approved)),
      icon: <CheckCircle2 size={20} />,
      tone: 'accent',
    },
    {
      title: 'Pagado',
      value: commissionService.formatCurrency(centsToDollars(summary?.paid)),
      icon: <Wallet size={20} />,
      tone: 'success',
    },
  ]

  const minPayoutCents = dashboard?.paymentInfo?.minPayout ?? 10000
  const approvedCents = summary?.approved || 0
  // Asesor cd-2b: su país tiene que tener definida la forma de pago (sucursal o transferencia).
  const pagoHabilitado = !esCd2b || !politica || Boolean(politica.liquidacion?.modalidad)
  const canRequestPayout = approvedCents >= minPayoutCents && pagoHabilitado
  const pais = partner?.country

  const fechaLocal = (d) => new Date(d).toLocaleDateString(convencion(pais).locale)

  // Una sola definición para la tabla y las tarjetas del teléfono (antes eran dos copias a mano).
  const columnas = [
    {
      clave: 'cliente', titulo: esCd2b ? 'Cliente' : 'Referido', prioridad: 'titulo',
      celda: (c) => <p className="truncate font-medium text-foreground">{c.referralId?.referredUser?.email || 'N/A'}</p>,
    },
    { clave: 'tipo', titulo: 'Tipo', prioridad: 'meta', celda: (c) => <Badge variant="outline">{TIPO[c.type] || c.type}</Badge> },
    {
      clave: 'monto', titulo: 'Monto', prioridad: 'valor',
      celda: (c) => (
        <div>
          <p className="font-semibold tabular-nums text-foreground">{commissionService.formatCurrency(c.amount)}</p>
          <p className="text-caption text-subtle">
            {c.percentage}% de {commissionService.formatCurrency(c.originalAmount)}
          </p>
        </div>
      ),
    },
    {
      clave: 'estado', titulo: 'Estado', prioridad: 'valor',
      celda: (c) => <Badge variant={statusVariants[c.status] || 'secondary'}>{commissionService.getStatusLabel(c.status)}</Badge>,
    },
    { clave: 'periodo', titulo: 'Período', prioridad: 'meta', celda: (c) => <span className="text-foreground">{c.period?.month}/{c.period?.year}</span> },
    { clave: 'fecha', titulo: 'Fecha', prioridad: 'meta', celda: (c) => <span className="text-subtle">{fechaLocal(c.createdAt)}</span> },
  ]

  return (
    <PageContainer>
      <PageHeader
        title="Comisiones"
        description={esCd2b ? 'En USD en todos los países. Se generan al cobrar y las aprueba un administrador.' : 'Historial y resumen de tus comisiones'}
        actions={
          <>
            <Button variant="outline" size="icon" className="sm:w-auto sm:px-4" aria-label="Historial de pagos" onClick={() => navigate('/commissions/payouts')}>
              <History size={16} /> <span className="hidden sm:inline">Historial</span>
            </Button>
            <Button variant="outline" size="icon" className="sm:w-auto sm:px-4" aria-label="Documentos fiscales" onClick={() => navigate('/commissions/documentos')}>
              <FileText size={16} /> <span className="hidden sm:inline">Documentos fiscales</span>
            </Button>
            <Button onClick={() => setPayoutOpen(true)} disabled={!canRequestPayout}>
              <Wallet size={14} /> Solicitar pago
            </Button>
          </>
        }
      />

      {esCd2b && politica && !pagoHabilitado && (
        <Alert variant="warning">
          <AlertDescription>
            <strong>Los retiros en {convencion(pais).nombre || pais} todavía no están habilitados.</strong>{' '}
            Tus comisiones se generan y se acumulan con normalidad; podrás retirarlas cuando el administrador defina si se pagan por la sucursal del país o por transferencia.
          </AlertDescription>
        </Alert>
      )}
      {esCd2b && politica?.liquidacion?.modalidad && (
        <p className="text-caption text-subtle">{MODALIDAD[politica.liquidacion.modalidad]}</p>
      )}

      {esGerente && (
        <div className="inline-flex rounded-full bg-surface-muted p-1" role="group" aria-label="Qué comisiones ver">
          {[['ventas', 'Mis ventas'], ['override', 'Override de equipo']].map(([v, t]) => (
            <button
              key={v}
              type="button"
              aria-pressed={ver === v}
              onClick={() => { setPage(0); if (v === 'override') params.set('ver', 'override'); else params.delete('ver'); setParams(params, { replace: true }) }}
              className={`min-h-9 rounded-full px-4 py-1.5 text-body-sm font-medium transition-colors ${ver === v ? 'bg-surface text-foreground shadow-sm' : 'text-subtle'}`}
            >
              {t}
            </button>
          ))}
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {kpis.map((k, i) => (
          <KpiCard key={i} {...k} loading={loading && !summary} />
        ))}
      </div>

      {!canRequestPayout && approvedCents > 0 && (
        <Alert variant="info">
          <AlertDescription>
            Necesitas al menos{' '}
            <strong>{commissionService.formatCurrency(centsToDollars(minPayoutCents))}</strong>{' '}
            en comisiones aprobadas para solicitar un pago. Actualmente tienes{' '}
            <strong>{commissionService.formatCurrency(centsToDollars(approvedCents))}</strong>.
          </AlertDescription>
        </Alert>
      )}

      <section className="space-y-3" aria-labelledby="titulo-historial-comisiones">
        <h3 id="titulo-historial-comisiones" className="font-display text-h3 font-semibold text-foreground">
          Historial de comisiones
        </h3>
        <DataTable
          etiquetaTabla="Historial de comisiones"
          columnas={columnas}
          filas={commissions}
          claveDeFila={(c) => c._id}
          cargando={loading}
          tarjetasEnMovil
          className="bg-card"
          vacio={
            <EmptyState
              icono={<Inbox size={24} />}
              titulo="Sin comisiones"
              descripcion={esCd2b
                ? (ver === 'override' ? 'Tu override aparece aquí cuando las empresas que vende tu equipo pagan.' : 'Tus comisiones aparecen aquí cuando las empresas que vendiste pagan.')
                : 'Las comisiones aparecerán aquí cuando tus referidos generen ingresos.'}
            />
          }
        />
        {!loading && commissions.length > 0 && (
          <Pagination
            className="rounded-lg border border-line bg-card"
            page={page}
            pageSize={rowsPerPage}
            total={total}
            onPageChange={setPage}
            onPageSizeChange={(s) => {
              setRowsPerPage(s)
              setPage(0)
            }}
          />
        )}
      </section>

      <Dialog open={payoutOpen} onOpenChange={setPayoutOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Solicitar pago</DialogTitle>
            <DialogDescription>
              Estás solicitando el pago de{' '}
              <strong>
                {commissionService.formatCurrency(centsToDollars(summary?.approved))}
              </strong>{' '}
              en comisiones aprobadas.
            </DialogDescription>
          </DialogHeader>

          <Textarea
            rows={3}
            placeholder="Notas o instrucciones especiales (opcional)…"
            value={payoutNotes}
            onChange={(e) => setPayoutNotes(e.target.value)}
          />

          <Alert variant="info">
            <AlertDescription>
              El pago se procesa manualmente y puede tomar entre 3-5 días hábiles.
            </AlertDescription>
          </Alert>

          <DialogFooter>
            <Button variant="outline" onClick={() => setPayoutOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleRequestPayout} disabled={requesting}>
              {requesting ? 'Enviando…' : 'Confirmar solicitud'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  )
}
